import { Download, ExternalLink, Play } from "lucide-react";
import { toast } from "sonner";
import { triggerDownload, cleanName } from "@/lib/download";

export default function CarouselGallery({ cards, base }) {
  if (!cards?.length) return null;
  return (
    <div
      data-testid="carousel-gallery"
      className="col-span-12 border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950"
    >
      <div className="p-5 border-b border-zinc-200 dark:border-zinc-800 flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-yellow-500 mb-1">
            // Carousel
          </p>
          <h4 className="font-display font-bold text-zinc-900 dark:text-white text-xl">
            {cards.length} cards in this ad
          </h4>
        </div>
        <button
          data-testid="download-all-cards-btn"
          onClick={() => {
            cards.forEach((c, i) => {
              const url = c.video_hd_url || c.video_sd_url || c.image_url;
              if (!url) return;
              const name = `${base}_card${i + 1}_${cleanName(c.title)}`;
              setTimeout(() => triggerDownload(url, name), i * 350);
            });
            toast.success(`Downloading ${cards.length} cards…`);
          }}
          className="inline-flex items-center gap-2 bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm h-10 px-4 transition-all hover:-translate-y-0.5"
        >
          <Download className="w-4 h-4" strokeWidth={2.5} />
          Download all
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {cards.map((card, i) => (
          <CarouselCard
            key={`card-${i}-${card.image_url || card.video_hd_url || i}`}
            card={card}
            index={i}
            base={base}
          />
        ))}
      </div>
    </div>
  );
}

function CarouselCard({ card, index, base }) {
  const media = card.image_url || card.video_preview_image_url;
  const hasVideo = Boolean(card.video_hd_url || card.video_sd_url);
  const downloadUrl = card.video_hd_url || card.video_sd_url || card.image_url;
  const filename = `${base}_card${index + 1}_${cleanName(card.title)}${hasVideo ? "" : "_thumb"}`;

  return (
    <article
      data-testid={`carousel-card-${index}`}
      className="border-r border-b border-zinc-200 dark:border-zinc-800 flex flex-col bg-white dark:bg-zinc-950"
    >
      <div className="relative aspect-square bg-black overflow-hidden group">
        {media ? (
          <img
            src={media}
            alt={card.title || `Card ${index + 1}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-zinc-500 text-xs font-mono uppercase tracking-widest">
            No media
          </div>
        )}
        <div className="absolute top-2 left-2 bg-black/85 backdrop-blur-sm px-2 py-1 border border-zinc-800">
          <span className="text-[10px] font-mono uppercase tracking-widest text-yellow-500">
            {hasVideo ? "VIDEO" : "IMAGE"} · {index + 1}
          </span>
        </div>
        {hasVideo && (
          <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
            <Play className="w-10 h-10 text-white" fill="currentColor" />
          </div>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col gap-2">
        {card.title && (
          <h5 className="font-display font-bold text-zinc-900 dark:text-white text-sm leading-tight line-clamp-2">
            {card.title}
          </h5>
        )}
        {card.body && (
          <p className="text-xs text-zinc-600 dark:text-zinc-400 leading-relaxed line-clamp-3">
            {card.body}
          </p>
        )}
        <div className="flex-1" />

        <div className="mt-2 flex items-center gap-2">
          {downloadUrl && (
            <button
              data-testid={`carousel-download-${index}`}
              onClick={() => {
                triggerDownload(downloadUrl, filename);
                toast.success(`Downloading card ${index + 1}`);
              }}
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold text-xs h-9 transition-all hover:-translate-y-0.5"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={2.5} />
              {hasVideo ? "Video" : "Image"}
            </button>
          )}
          {card.link_url && (
            <a
              href={card.link_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`carousel-link-${index}`}
              className="h-9 w-9 grid place-items-center border border-zinc-300 dark:border-zinc-800 text-zinc-600 dark:text-zinc-400 hover:border-yellow-500 hover:text-yellow-500 transition-colors"
              title={card.cta_text || "Open landing page"}
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
        {card.cta_text && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 dark:text-zinc-600">
            CTA · {card.cta_text}
          </div>
        )}
      </div>
    </article>
  );
}
